import React, { useState, useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'

const ease = [0.16, 1, 0.3, 1] as const

const colors = {
  teal: '#145F6C',
  tealDark: '#0D3135',
  copper: '#AB9381',
  cream: '#F7F8F6',
  gold: '#C9A962',
}

// FAQ content from Hudson FAQ.md - grouped by section
const faqSections: { title: string; items: { q: string; a: string }[] }[] = [
  {
    title: 'About Hudson',
    items: [
      {
        q: 'What is proactive home management, and how is Hudson My Home different from a traditional handyman service?',
        a: 'Traditional handyman services are reactive—they wait for things to break before fixing them, which can cost you time, money, and peace of mind. Hudson My Home is a premium, proactive home management service focused on preventing problems before they start. Instead of one-off, task-based fixes, you are paired with a dedicated Hudson Home Specialist who conducts regular monthly maintenance visits to proactively maintain, repair, and optimize your property.',
      },
      {
        q: 'Why do homeowners choose Hudson My Home instead of managing maintenance themselves?',
        a: 'Homeownership comes with hundreds of small tasks that are easy to overlook until they become expensive problems. Hudson My Home removes that mental load. Instead of tracking maintenance schedules, researching contractors, and managing repairs, you get a Hudson — a dedicated home professional proactively maintaining your home year-round.',
      },
      {
        q: 'Is Hudson My Home the same as property management?',
        a: 'No. Property management companies manage rental properties for landlords. Hudson My Home is designed specifically for homeowners who live in their homes and want a concierge-level service to maintain and protect their property proactively.',
      },
      {
        q: 'How much does reactive home maintenance really cost compared to a proactive plan?',
        a: 'Reactive home maintenance is often significantly more expensive than proactive care. Small issues—like a clogged air filter or a slow plumbing leak—can quietly damage major systems over time and lead to costly repairs. Proactive maintenance helps extend the life of your home\'s systems, reduce unexpected emergencies, and protect the long-term value of your property.',
      },
    ],
  },
  {
    title: 'Membership',
    items: [
      {
        q: 'What exactly is included in a Hudson My Home membership?',
        a: 'Our membership includes a full home assessment with a dedicated Hudson Home Specialist, monthly or quarterly home care visits, comprehensive proactive maintenance, and vendor coordination. We create a MyHome Blueprint™ for your home outlining the next 5 years of maintenance needs and goals. Our goal is to be your single point of contact for everything your home needs. No more trying to figure out who to call, what needs to be done, or finding the time to do it. We know you can, but sometimes you can\'t.',
      },
      {
        q: 'What types of maintenance tasks does Hudson My Home handle?',
        a: 'Hudson My Home handles a wide range of preventative maintenance tasks including seasonal home care, mechanical system checks, filter replacements, water system inspections, exterior maintenance, and general upkeep designed to prevent future problems. Your MyHome Blueprint™ outlines exactly what maintenance tasks are scheduled for your home over time.',
      },
      {
        q: 'How do I sign up?',
        a: 'You can easily reserve your spot online or book a free 15-minute Discovery Call. During this no-obligation, concierge-style call, a Hudson Home Consultant will review your specific home ownership frustrations, explain how our proactive home management system works, and demonstrate how we can save you both time and money.',
      },
      {
        q: 'What areas do you currently serve?',
        a: 'We currently serve the Greater Des Moines, Iowa metro area, including Ankeny and surrounding neighborhoods. Our goal is to provide premier, concierge-style preventative home care to local homeowners who want to protect their real estate investments and enjoy their weekends. We will soon be expanding to other markets! If you are in a different market, reach out to us and share you need a Hudson for your home!',
      },
      {
        q: 'Do I have to sign a long-term contract or cancellation policy?',
        a: 'No. Hudson My Home operates as a monthly subscription service, and you can cancel at any time with 30 days\' notice. We also offer a 100% satisfaction promise in your first 90 days—if you are not completely satisfied, we will make it right, or you can receive a full refund within your first 30 days.',
      },
      {
        q: 'How much does a Hudson My Home membership cost?',
        a: 'Hudson My Home memberships are designed to be a predictable monthly investment in protecting your home. Pricing varies based on the size of your home and the complexity of your individual needs. After your initial assessment, we create a custom maintenance plan and membership designed specifically for your home needs. See our Pricing page for current options.',
      },
      {
        q: 'Are repair costs or materials included in the membership?',
        a: 'Your membership covers proactive maintenance visits, routine home care, planning, and vendor coordination. If repairs require materials or specialized contractors, we coordinate the work and provide transparent pricing before anything proceeds.',
      },
      {
        q: 'What happens if my home needs a larger repair or project?',
        a: 'If your home requires a larger repair or improvement, Hudson coordinates the project from start to finish. We help scope the work, connect you with trusted licensed professionals, schedule the work, and ensure the project aligns with your home\'s long-term maintenance plan.',
      },
    ],
  },
  {
    title: 'Service Process',
    items: [
      {
        q: 'How do I get started? What does the process look like?',
        a: 'Getting started begins with a call with a Hudson Home Consultant to learn more about you, your home, and our program. We will schedule a whole home assessment to determine your home\'s unique needs and build a custom plan to proactively care for your home and reduce your mental load. We will prioritize your home\'s needs over the next five years, starting with safety and security, followed by damage prevention, mechanical systems, and finally, your home improvement goals. Then we will start monthly visits to your home to ensure full implementation of your home\'s unique plan.',
      },
      {
        q: 'What happens during a monthly Hudson Home visit?',
        a: 'During each visit, your Hudson Home Specialist performs preventative maintenance tasks, system checks, seasonal upkeep, and progress toward your MyHome Blueprint™ maintenance plan. Over time, these visits keep your home\'s mechanical systems, structure, and seasonal needs proactively managed.',
      },
      {
        q: 'Do I need to be home during service visits?',
        a: 'No. Many members choose to provide secure access to their home so our specialists can complete maintenance visits while they are away. All Hudson team members are background-checked, insured, and trained to treat your home with the highest level of professionalism and respect.',
      },
      {
        q: 'What types of homes are a good fit for Hudson My Home?',
        a: 'Hudson My Home is designed for homeowners who want proactive care for their property. Our members are often busy professionals, families, or homeowners with larger or more complex properties who prefer preventative management over reactive repairs.',
      },
    ],
  },
  {
    title: 'Home Emergencies',
    items: [
      {
        q: 'What happens if I have an unexpected home emergency?',
        a: 'Your peace of mind is our priority. With our MyHome Guarantee™, we guarantee a same-day response for urgent maintenance issues. When something goes wrong, you don\'t have to scramble to find help—you already know exactly who is handling it.',
      },
    ],
  },
  {
    title: 'Vendors and Specialists',
    items: [
      {
        q: 'Do I still need to find, hire, and schedule contractors for specialized repairs or upgrades?',
        a: 'No, we handle the vendor scheduling and management for you. If a project requires specialized work (such as a licensed plumber or electrician), our team acts as your liaison to ensure trusted professionals handle the job with clear timing. This includes full vendor coordination for extra services like landscaping, snow removal, pest control, and seasonal deep cleaning.',
      },
      {
        q: 'Are your Home Specialists and vendor partners licensed and insured?',
        a: 'Absolutely. Hudson My Home maintains full liability and workers\' compensation insurance. Furthermore, our Home Specialists and any vetted vendor partners we coordinate for specialized work hold all the required trade licenses. You can rest easy knowing your most valuable asset is protected by verified professionals.',
      },
      {
        q: 'Can Hudson work with my existing vendors?',
        a: 'Absolutely. If you already have trusted professionals you prefer, we can coordinate with them as part of your maintenance plan.',
      },
    ],
  },
]

const FaqItem: React.FC<{ q: string; a: string; isOpen: boolean; onToggle: () => void }> = ({ q, a, isOpen, onToggle }) => (
  <div className="border-b border-grey-100 last:border-0">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-4 py-6 text-left group"
      aria-expanded={isOpen}
    >
      <span className="font-medium text-grey-900 text-base sm:text-base pr-4 group-hover:text-grey-700 transition-colors">
        {q}
      </span>
      <span
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
        style={{ backgroundColor: isOpen ? colors.copper : 'rgba(0,0,0,0.06)', color: isOpen ? '#fff' : colors.copper }}
      >
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </span>
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.35, ease }}
      className="overflow-hidden"
    >
      <div className="pb-6 pl-0 pr-12">
        <p className="text-base leading-relaxed text-grey-600">{a}</p>
      </div>
    </motion.div>
  </div>
)

const FAQsPage: React.FC = () => {
  const [openKeys, setOpenKeys] = useState<Record<string, boolean>>({})
  const ref = useRef(null)
  const ctaRef = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })
  const ctaInView = useInView(ctaRef, { once: true, margin: '-60px' })

  const toggle = (sectionIndex: number, itemIndex: number) => {
    const key = `${sectionIndex}-${itemIndex}`
    setOpenKeys(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="min-h-screen bg-white">
      <MetaTags
        title="Frequently Asked Questions | Hudson My Home"
        description="Common questions about Hudson My Home membership, pricing, service areas, and what makes us different from a handyman."
      />
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-24">
        {/* Hero */}
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 pt-12 pb-16">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-sm tracking-[0.3em] uppercase"
              style={{ color: colors.copper }}
            >
              Support
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease }}
              className="font-serif text-grey-900 mt-3 mb-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-lg text-grey-500"
            >
              Everything you need to know about proactive home management and your Hudson My Home membership.
            </motion.p>
          </div>
        </section>

        {/* FAQ by section */}
        <section ref={ref} className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-16">
          {faqSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: sectionIndex * 0.05 }}
              className="mb-16"
            >
              <h2
                className="text-sm tracking-[0.35em] uppercase mb-6"
                style={{ color: colors.copper }}
              >
                {section.title}
              </h2>
              <div className="rounded-xl border border-grey-100 overflow-hidden bg-white">
                {section.items.map((item, itemIndex) => (
                  <FaqItem
                    key={itemIndex}
                    q={item.q}
                    a={item.a}
                    isOpen={openKeys[`${sectionIndex}-${itemIndex}`] ?? false}
                    onToggle={() => toggle(sectionIndex, itemIndex)}
                  />
                ))}
              </div>
            </motion.div>
          ))}

          {/* CTA below FAQ */}
          <motion.div
            ref={ctaRef}
            initial={{ opacity: 0, y: 40 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease }}
            className="mt-20 text-center"
          >
            <p className="text-grey-600 mb-6 text-lg">
              Ready to take the hardship out of homeownership?
            </p>
            <Link
              to="/membership"
              className="group inline-flex items-center justify-center gap-3 px-10 py-5 text-lg font-medium text-white transition-all duration-500 hover:shadow-xl"
              style={{ backgroundColor: colors.copper }}
            >
              Discover Home Membership
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-500" />
            </Link>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default FAQsPage
